"use client";

import type { Avatar } from "@repo/shared/types/avatar";
import type { CustomIconName } from "@repo/ui/components/icon";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  DEFAULT_COLOR,
  DEFAULT_ICON,
  ORDERED_ICONS,
} from "./avatar-picker.constants";
import { searchEmojis, searchIcons } from "./avatar-picker.helpers";
import type { EmojiCategory } from "./avatar-picker.types";

// --- Context shape ---

interface AvatarPickerContextValue {
  // Search
  searchQuery: string | null;
  setSearchQuery: (query: string | null) => void;
  isSearching: boolean;

  // Icon state
  selectedIcon: CustomIconName;
  setIcon: (icon: CustomIconName) => void;
  iconColor: string;
  setIconColor: (color: string) => void;
  iconsList: { icon: CustomIconName; disabled: boolean }[];

  // Emoji state
  selectedEmoji: string | null;
  setEmoji: (emoji: string) => void;
  emojiCategory: EmojiCategory;
  setEmojiCategory: (category: EmojiCategory) => void;
  emojiSearchResults: ReturnType<typeof searchEmojis> | null;

  // Actions
  clearAvatar: () => void;
}

const AvatarPickerContext = createContext<AvatarPickerContextValue | null>(
  null,
);

export function useAvatarPicker() {
  const ctx = useContext(AvatarPickerContext);
  if (!ctx) {
    throw new Error(
      "useAvatarPicker must be used within <AvatarPickerProvider>",
    );
  }
  return ctx;
}

// --- Provider ---

interface ProviderProps {
  value?: Avatar | null;
  onChange: (avatar: Avatar | null) => void;
  children: React.ReactNode;
}

export function AvatarPickerProvider({
  value,
  onChange,
  children,
}: ProviderProps) {
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [emojiCategory, setEmojiCategory] = useState<EmojiCategory>("people");

  const selectedIcon =
    ((value?.type === "icon" ? value.icon : undefined) as CustomIconName) ||
    DEFAULT_ICON;
  const iconColor =
    (value?.type === "icon" ? value.color : undefined) || DEFAULT_COLOR;
  const selectedEmoji =
    (value?.type === "emoji" ? value.emoji : undefined) || null;

  const isSearching = searchQuery !== null && searchQuery.length > 0;

  // Icons list (with search filtering)
  const iconsList = useMemo(() => {
    if (!isSearching) {
      return ORDERED_ICONS.map((icon) => ({ icon, disabled: false }));
    }
    const activeIcons = searchIcons(searchQuery ?? "");
    return ORDERED_ICONS.map((icon) => ({
      icon,
      disabled: !activeIcons.has(icon),
    }));
  }, [searchQuery, isSearching]);

  // Emoji search results
  const emojiSearchResults = useMemo(() => {
    if (!searchQuery) return null;
    return searchEmojis(searchQuery);
  }, [searchQuery]);

  const setIcon = useCallback(
    (icon: CustomIconName) => {
      onChange({ type: "icon", icon, color: iconColor });
    },
    [onChange, iconColor],
  );

  const setIconColor = useCallback(
    (color: string) => {
      onChange({
        type: "icon",
        icon: selectedIcon,
        color: color.toLowerCase(),
      });
    },
    [onChange, selectedIcon],
  );

  const setEmoji = useCallback(
    (emoji: string) => {
      onChange({ type: "emoji", emoji });
    },
    [onChange],
  );

  const clearAvatar = useCallback(() => {
    onChange(null);
  }, [onChange]);

  const ctx = useMemo<AvatarPickerContextValue>(
    () => ({
      searchQuery,
      setSearchQuery,
      isSearching,
      selectedIcon,
      setIcon,
      iconColor,
      setIconColor,
      iconsList,
      selectedEmoji,
      setEmoji,
      emojiCategory,
      setEmojiCategory,
      emojiSearchResults,
      clearAvatar,
    }),
    [
      searchQuery,
      isSearching,
      selectedIcon,
      setIcon,
      iconColor,
      setIconColor,
      iconsList,
      selectedEmoji,
      setEmoji,
      emojiCategory,
      emojiSearchResults,
      clearAvatar,
    ],
  );

  return (
    <AvatarPickerContext.Provider value={ctx}>
      {children}
    </AvatarPickerContext.Provider>
  );
}
